import { useState } from 'react';
import {
  Box,
  Button,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

const HEIGHT = 40;
const WIDTH = 40;

const BOX_WIDTH = `${WIDTH}px`;
const BOX_HEIGHT = `${HEIGHT}px`;

const STEPPER_DIVIDER_MODE = {
  START: 'start',
  END: 'end',
  NONE: 'none',
  NONE_START: 'noneStart',
  NONE_END: 'noneEnd',
};

export const BACK_BUTTON_LAYOUT = {
  TREE: 'tree',
  PARENT: 'parent',
};

export function findNodeById(nodes, id) {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];

    if (node.id === id) {
      return node;
    }

    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

function findNodesById(nodes, id) {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];

    if (node.id === id) {
      return nodes;
    }

    if (node.children) {
      const found = findNodesById(node.children, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

function findAncestorPath(nodes, id, path = []) {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];

    if (node.id === id) {
      return path;
    }

    if (node.children) {
      const found = findAncestorPath(node.children, id, [...path, node]);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

function getPathExpandedTreeItems(
  nodes,
  expandableIds,
  selectedId,
  immediateParentId,
  depth = 0,
  parentPath = '',
) {
  if (!Array.isArray(nodes)) {
    return [];
  }

  return nodes.flatMap(node => {
    const path = parentPath ? `${parentPath}/${node.id}` : node.id;
    const item = {
      node,
      depth,
      path,
      isSelected: node.id === selectedId,
    };

    let childNodes = null;
    if (expandableIds.has(node.id) && node.children?.length) {
      childNodes = node.children;
    } else if (node.id === immediateParentId && node.children?.length) {
      const currentNode = node.children.find(child => child.id === selectedId);
      childNodes = currentNode ? [currentNode] : null;
    }

    const childItems = childNodes
      ? getPathExpandedTreeItems(
          childNodes,
          expandableIds,
          selectedId,
          immediateParentId,
          depth + 1,
          path,
        )
      : [];
    return [item, ...childItems];
  });
}

function resolveParentEntry(parentEntry, config) {
  if (!parentEntry) {
    return null;
  }

  const referencedNode = parentEntry.element
    ? findNodeById(config, parentEntry.element)
    : null;
  const { element, ...overrides } = parentEntry;
  const node = {
    id: referencedNode?.id,
    label: referencedNode?.label,
    icon: referencedNode?.icon,
    href: referencedNode?.href,
    ...overrides,
  };

  if (!node.label || !node.href) {
    return null;
  }

  return node;
}

function collectParentEntries(ancestors) {
  const entries = [];
  const seen = new Set();

  ancestors.forEach(ancestor => {
    const parentEntries =
      Array.isArray(ancestor.parent) && ancestor.parent.length > 0
        ? ancestor.parent
        : [{ element: ancestor.id }];

    parentEntries.forEach(entry => {
      const key = entry.element || entry.id;
      if (key && seen.has(key)) {
        return;
      }
      if (key) {
        seen.add(key);
      }
      entries.push(entry);
    });
  });

  return entries;
}

function getParentTreeItems({ config, ancestors }) {
  const parentEntries = collectParentEntries(ancestors);

  return parentEntries.reduce((items, parentEntry, index) => {
    const node = resolveParentEntry(parentEntry, config);

    if (node) {
      items.push({
        node,
        depth: index,
        path: node.id || parentEntry.element || `parent-${index}`,
        isSelected: false,
      });
    }

    return items;
  }, []);
}

function StepperDivider({
  mode = STEPPER_DIVIDER_MODE.NONE,
  selected = false,
  color = 'primary',
}) {
  const theme = useTheme();
  const palette = theme.palette[color] || theme.palette.primary;
  const backgroundColor = selected
    ? palette.dark || palette.main
    : palette.light || palette.main;
  let item = null;

  const arrowHeadStyle = {
    clipPath: 'polygon(0% 0%, 50% 50%, 0% 100%)',
  };

  const arrowBackStyle = {
    clipPath: 'polygon(0% 0%, 101% 0%, 101% 100%, 0% 100%, 50% 50%)',
  };

  switch (mode) {
    case STEPPER_DIVIDER_MODE.START: {
      item = (
        <Box
          sx={{
            width: `${WIDTH - 15}px`,
            height: BOX_HEIGHT,
            backgroundColor,
            ...arrowBackStyle,
          }}
        />
      );
      break;
    }
    case STEPPER_DIVIDER_MODE.END: {
      item = (
        <Box
          sx={{
            width: `${WIDTH - 15}px`,
            height: BOX_HEIGHT,
            backgroundColor,
            ...arrowHeadStyle,
          }}
        />
      );
      break;
    }
    default: {
      const noneStartStyle = {
        borderRadius: '8px 0px 0px 8px',
      };
      const nonEndStyle = {
        borderRadius: '0px 8px 8px 0px',
      };
      item = (
        <Box
          sx={{
            width: BOX_WIDTH,
            height: BOX_HEIGHT,
            backgroundColor,
            ...(mode === STEPPER_DIVIDER_MODE.NONE_START && noneStartStyle),
            ...(mode === STEPPER_DIVIDER_MODE.NONE_END && nonEndStyle),
          }}
        />
      );
    }
  }
  return item;
}

function AncestorTreeMenu({ items, anchorEl, onClose, onNavigate }) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 220,
            mt: 0.5,
          },
        },
      }}>
      {items.map(({ node, depth, path, isSelected }) => (
        <MenuItem
          key={path}
          selected={isSelected}
          onClick={() => onNavigate(node)}
          sx={{
            pl: 2 + depth * 2.5,
            py: 1,
            '&.Mui-selected': {
              bgcolor: 'primary.light',
              color: 'primary.dark',
              '&:hover': {
                bgcolor: 'primary.light',
              },
            },
          }}>
          {node.icon && (
            <ListItemIcon
              sx={{
                minWidth: 32,
                color: isSelected ? 'primary.dark' : 'inherit',
              }}>
              <node.icon fontSize="small" />
            </ListItemIcon>
          )}
          <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
            <FormattedMessage
              id={node.label.id}
              defaultMessage={node.label.defaultMessage}
            />
          </Typography>
        </MenuItem>
      ))}
    </Menu>
  );
}

function BackToStepperTabButton({
  onClick,
  tooltipTitle,
  open = false,
  hasMenu = false,
  children,
}) {
  const theme = useTheme();

  return (
    <Tooltip arrow placement="top" title={tooltipTitle}>
      <Stack
        component={Button}
        color="tertiary"
        onClick={onClick}
        direction="row"
        alignItems="center"
        aria-haspopup={hasMenu ? 'true' : undefined}
        aria-expanded={hasMenu && open ? 'true' : undefined}
        sx={{
          flex: '0 0 auto',
          minWidth: 0,
          p: 0,
          minHeight: BOX_HEIGHT,
          boxShadow: 'none',
          backgroundColor: 'transparent',
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: 'transparent',
          },
        }}>
        <StepperDivider
          mode={STEPPER_DIVIDER_MODE.START}
          selected={false}
          color="tertiary"
        />
        <Stack
          gap={0.2}
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            flex: 1,
            minWidth: 0,
            px: 1.5,
            textAlign: 'center',
            alignContent: 'center',
            height: BOX_HEIGHT,
            lineHeight: BOX_HEIGHT,
            backgroundColor: theme.palette.tertiary.main,
            color: theme.palette.tertiary.contrastText,
          }}>
          {children}
        </Stack>
        <StepperDivider
          selected={false}
          mode={STEPPER_DIVIDER_MODE.NONE_END}
          color="tertiary"
        />
      </Stack>
    </Tooltip>
  );
}

function BackToDropdown({ items }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <BackToStepperTabButton
        hasMenu
        open={open}
        tooltipTitle={
          <FormattedMessage id="BACK_TO" defaultMessage="Back to" />
        }
        onClick={event => {
          setAnchorEl(event.currentTarget);
        }}>
        <Typography variant="body2" noWrap>
          <FormattedMessage id="BACK_TO" defaultMessage="Back to" />
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </BackToStepperTabButton>
      <AncestorTreeMenu
        items={items}
        anchorEl={anchorEl}
        onClose={handleClose}
        onNavigate={node => {
          handleClose();
          if (node?.href) {
            navigate(node.href);
          }
        }}
      />
    </>
  );
}

function BackToTreeTab({ config, ancestors, selectedId }) {
  const immediateParentId = ancestors[ancestors.length - 1]?.id;
  const expandableIds = new Set(
    ancestors.slice(0, -1).map(ancestor => ancestor.id),
  );
  const treeItems = getPathExpandedTreeItems(
    config,
    expandableIds,
    selectedId,
    immediateParentId,
  );

  return <BackToDropdown items={treeItems} />;
}

function ArrowError({ children }) {
  const sx = {
    width: '18px',
    height: '18px',
    borderRadius: '9px',
    backgroundColor: '#FF0000',
    fontSize: '11px',
    color: '#ffffff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ml: 1,
  };
  return <Box sx={sx}>{children}</Box>;
}

function SmartStepperTabs({
  config,
  stepperDisableExceptFirst = false,
  selectedId = null,
  backButtonLayout = BACK_BUTTON_LAYOUT.TREE,
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  const selectedStepperId = selectedId || config[0]?.id;
  const stepperItems = findNodesById(config, selectedStepperId) || [];
  const ancestors = findAncestorPath(config, selectedStepperId) || [];
  const hasAncestors = ancestors.length > 0;
  const isParentBackLayout = backButtonLayout === BACK_BUTTON_LAYOUT.PARENT;
  const parentItems = isParentBackLayout
    ? getParentTreeItems({
        config,
        ancestors,
      })
    : [];
  const showBackButton = isParentBackLayout
    ? parentItems.length > 0
    : hasAncestors;

  return (
    <Stack
      direction="row"
      sx={{
        width: '100%',
        background: theme.palette.primary.light,
        borderRadius: '8px',
        mt: 2,
        mb: 1,
      }}>
      {stepperItems.map((item, index) => {
        const selected = item.id === selectedStepperId;
        const disableClick = stepperDisableExceptFirst && index > 0;
        const isFirstVisible = index === 0;
        const isLastVisible =
          !showBackButton && index === stepperItems.length - 1;
        const errorCount = item?.getErrorCount ? item?.getErrorCount() : null;

        return (
          <Tooltip
            arrow
            placement="top"
            title={
              <FormattedMessage
                id={item.label.id}
                defaultMessage={item.label.defaultMessage}
              />
            }
            key={item.id}>
            <Stack
              key={item.id}
              component={Button}
              onClick={() => {
                if (!disableClick) {
                  navigate(item?.href);
                }
              }}
              direction="row"
              alignItems="center"
              sx={{
                flex: 1,
                minWidth: 0,
                p: 0,
                minHeight: BOX_HEIGHT,
              }}>
              <StepperDivider
                mode={
                  isFirstVisible
                    ? STEPPER_DIVIDER_MODE.NONE_START
                    : STEPPER_DIVIDER_MODE.START
                }
                selected={selected}
                index={index}
              />
              <Stack
                gap={0.2}
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'center',
                  alignContent: 'center',
                  height: BOX_HEIGHT,
                  lineHeight: BOX_HEIGHT,
                  backgroundColor: selected
                    ? theme.palette.primary.dark
                    : theme.palette.primary.light,
                  color: selected ? 'primary.contrastText' : 'primary.dark',
                }}>
                {item.icon && <item.icon fontSize="18px" />}
                <Typography variant="body2" noWrap>
                  <FormattedMessage
                    id={item.label.id}
                    defaultMessage={item.label.defaultMessage}
                  />
                </Typography>
                {errorCount && <ArrowError>{errorCount}</ArrowError>}
              </Stack>
              <StepperDivider
                index={index}
                selected={selected}
                mode={
                  isLastVisible
                    ? STEPPER_DIVIDER_MODE.NONE_END
                    : STEPPER_DIVIDER_MODE.END
                }
              />
            </Stack>
          </Tooltip>
        );
      })}
      {showBackButton &&
        (isParentBackLayout ? (
          <BackToDropdown items={parentItems} />
        ) : (
          <BackToTreeTab
            config={config}
            ancestors={ancestors}
            selectedId={selectedStepperId}
          />
        ))}
    </Stack>
  );
}

export default SmartStepperTabs;
